import React from 'react';

// components
import { ButtonComponent } from 'baseComponents/ButtonComponent';

// styles
import './useExistingAudienceTable.scss';

// helpers
import { ExistingFilterGroup } from '../ExistingFilterGroup';
import { AttributeType } from 'types/create-campaign';
import { RemoveAudienceModal } from '../RemoveAudienceModal';
import { useRemoveAudience } from 'hooks/useRemoveAudience';
import { useGetAudienceList } from 'hooks/useGetAudienceList';
import { notification } from 'antd';
import {
  useCreateCampaignStore,
  useCreateCampaignStoreActions,
} from 'store/CreateCampaignStore';
import { FullScreenLoaderComponent } from 'baseComponents/FullScreenLoaderComponent';
import { EstimationChannelCard } from '../EstimationChannelCard';
import { TextComponent } from 'baseComponents/TextComponent';

interface Props {
  chosenAudience: any;
  attributesList: AttributeType[];
  setChosenAudience: (audience: any) => void;
  estimation?: Record<
    'sms' | 'push',
    {
      estimated_reachable_users: number;
      has_credits: boolean;
      credit_cost: number;
    } | null
  >;
  onChannelSelect: (channel: 'sms' | 'push') => void;
  onPaymentModalOpen: () => void;
  loading?: boolean;
}

export const UseExistingAudienceTable: React.FC<Props> = (props) => {
  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const [api, contextHolder] = notification.useNotification();

  const { setAudience } = useCreateCampaignStoreActions();
  const audience = useCreateCampaignStore((state) => state.audience);

  const isBlankTemplate = props.chosenAudience
    ? Object.keys(props.chosenAudience?.audience_filters).length === 0
    : false;

  const { refetch: refetchAudienceData } = useGetAudienceList();

  const { mutate: removeAudience, isPending: isRemoveAudiencePending } =
    useRemoveAudience({
      onSuccess: () => {
        refetchAudienceData();
        setIsModalOpen(false);
        props.setChosenAudience(null);
        setAudience(null);

        return api.success({
          message: 'Audience has been removed',
          description: 'Audience has been successfully removed.',
          duration: 3,
        });
      },
      onError: () => {
        return api.error({
          message: 'Something went wrong',
          description:
            'Something went wrong with removing audience. Please try again later.',
          duration: 3,
        });
      },
    });

  const handleRemoveAudience = () => {
    removeAudience({ audience_id: props.chosenAudience.id });
  };

  return (
    <div className='existing-audience-wrapper'>
      {contextHolder}
      {isBlankTemplate ? (
        <div className='with-border'>
          This audience is addressed to all users from selected stores who have
          not received an SMS in the last 3 days and have not purchased anything
          in the last 6 days.
        </div>
      ) : (
        <ExistingFilterGroup
          filtersGroup={props.chosenAudience?.audience_filters?.AND}
          attributesList={props.attributesList}
        />
      )}
      <div className='estimation-channel-cards-wrapper-container'>
        <TextComponent
          text='Select a channel'
          size='large'
          weight='extra-bold'
          center
        />
        <div className='estimation-channel-cards-wrapper'>
          <EstimationChannelCard
            title='SMS'
            disabled={!props.estimation?.sms || !audience}
            loading={props.loading}
            hasCredits={props.estimation?.sms?.has_credits || false}
            estimatedAudience={
              props.estimation?.sms?.estimated_reachable_users
            }
            onClick={() => {
              if (props.estimation?.sms?.has_credits) {
                props.onChannelSelect('sms');
              } else {
                props.onPaymentModalOpen();
              }
            }}
          />
          <EstimationChannelCard
            title='Push Notification'
            disabled={!props.estimation?.push || !audience}
            loading={props.loading}
            hasCredits={props.estimation?.push?.has_credits || false}
            estimatedAudience={
              props.estimation?.push?.estimated_reachable_users
            }
            onClick={() => {
              if (props.estimation?.push?.has_credits) {
                props.onChannelSelect('push');
              } else {
                props.onPaymentModalOpen();
              }
            }}
          />
        </div>
      </div>
      {!isBlankTemplate && props.chosenAudience && (
        <div className='remove-btn-wrapper'>
          <ButtonComponent
            label='Remove audience'
            onClick={() => setIsModalOpen(true)}
            appereance='warning'
            size='large'
            uppercase
            fullWidth
          />
        </div>
      )}
      <FullScreenLoaderComponent spinning={isRemoveAudiencePending} />
      <RemoveAudienceModal
        isModalOpen={isModalOpen}
        closeModal={() => setIsModalOpen(false)}
        handleRemoveAudience={handleRemoveAudience}
      />
    </div>
  );
};
